import {
  HStack,
  IconButton,
  Input,
  Separator,
  Spacer,
  Stack,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { LuPlus, LuX } from "react-icons/lu";

import { FacebookAdAccount } from "../types/facebook";
import {
  RetargetingRuleNode,
  RetargetingRuleNodeJunction,
  RetargetingRuleNodeMode,
  RetargetingRuleNodeRoot,
  RetargetingRuleNodeURL,
  isURLNode,
  pushJunctionNodeChild,
  pushRootNodeChild,
  removeJunctionNodeChild,
  removeRootNodeChild,
  replaceJunctionNodeChild,
  replaceRootNodeChild,
  setJunctionNodeType,
  setURLNodeOperator,
} from "../types/retargeting-rule";
import { Button } from "./button";
import ConnectFacebookHelperText from "./connect-facebook-helper-text";
import { Field } from "./field";
import { InputGroup } from "./input-group";
import { ProgressCircleRing, ProgressCircleRoot } from "./progress-circle";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

export interface RetargetingRuleFormData {
  name: string;
  rules: RetargetingRuleNodeRoot;
  fbAdAccountId: string[];
}

export interface RetargetingRuleFormProps {
  adAccounts: FacebookAdAccount[];
  canChangeAdAccount?: boolean;
  canChangeName?: boolean;
  defaultValue?: RetargetingRuleFormData;
  loadingAdAccounts: boolean;
  onSubmit: (data: RetargetingRuleFormData) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
  wrap?: (content: React.ReactNode) => React.ReactNode;
}

const defaultRules: RetargetingRuleNodeRoot = {
  children: [
    {
      mode: RetargetingRuleNodeMode.INCLUDE,
      type: "and",
      retention: 30,
      children: [
        {
          operator: "contains",
          field: "url",
          value: "",
        },
      ],
    },
  ],
};

export default function RetargetingRuleForm(props: RetargetingRuleFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RetargetingRuleFormData>({
    defaultValues: props.defaultValue ?? {
      rules: defaultRules,
    },
  });
  const onSubmit = handleSubmit(props.onSubmit);

  const adAccounts = React.useMemo(() => {
    return createListCollection({
      items: props.adAccounts.map((act) => ({
        label: act.name,
        value: act.id,
      })),
    });
  }, [props.adAccounts]);

  let content: React.ReactNode = (
    <Stack gap="3">
      <Field
        label="Audience Name"
        invalid={!!errors.name}
        errorText={errors.name?.message}
      >
        <Input
          placeholder="An Audience"
          {...register("name", {
            disabled: !(props.canChangeName ?? true),
            required: "Please provide an audience name.",
          })}
        />
      </Field>
      <Field
        label="Facebook Ad Account"
        invalid={!!errors.fbAdAccountId}
        errorText={errors.fbAdAccountId?.message}
        helperText={
          adAccounts.items.length === 0 && <ConnectFacebookHelperText />
        }
      >
        <Controller
          name="fbAdAccountId"
          rules={{ required: true }}
          control={control}
          render={({ field }) => {
            return (
              <HStack w="full">
                <SelectRoot
                  name={field.name}
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  onInteractOutside={field.onBlur}
                  collection={adAccounts}
                  disabled={
                    adAccounts.items.length === 0 ||
                    !(props.canChangeAdAccount ?? true)
                  }
                >
                  <SelectTrigger>
                    <SelectValueText placeholder="Select Facebook Ad Account" />
                  </SelectTrigger>
                  <SelectContent portalRef={props.portalRef}>
                    {adAccounts.items.map((act) => (
                      <SelectItem item={act} key={act.value}>
                        {act.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
                {props.loadingAdAccounts && (
                  <ProgressCircleRoot value={null}>
                    <ProgressCircleRing />
                  </ProgressCircleRoot>
                )}
              </HStack>
            );
          }}
        />
      </Field>
      <Separator mt="2" mb="2" size="md" variant="dashed" />
      <Controller
        name="rules"
        rules={{ required: true }}
        control={control}
        render={({ field }) => (
          <RootRuleEditor
            root={field.value}
            onChange={(node) => field.onChange(node)}
            portalRef={props.portalRef}
          />
        )}
      />
    </Stack>
  );
  if (props.wrap !== undefined) {
    content = props.wrap(content);
  }

  return <form onSubmit={onSubmit}>{content}</form>;
}

interface RootRuleEditorProps {
  root: RetargetingRuleNodeRoot;
  onChange: (root: RetargetingRuleNodeRoot) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
}

function RootRuleEditor({ root, onChange, portalRef }: RootRuleEditorProps) {
  return (
    <Stack width="full">
      <HStack>
        <Text fontWeight="semibold">Rules</Text>
        <Spacer />
        <Button
          colorPalette="gray"
          fontSize="xs"
          fontWeight="normal"
          variant="surface"
          size="sm"
          onClick={() =>
            onChange(pushRootNodeChild(root, RetargetingRuleNodeMode.INCLUDE))
          }
        >
          <LuPlus />
          Include People
        </Button>
        <Button
          colorPalette="gray"
          fontSize="xs"
          fontWeight="normal"
          variant="surface"
          size="sm"
          onClick={() =>
            onChange(pushRootNodeChild(root, RetargetingRuleNodeMode.EXCLUDE))
          }
        >
          <LuPlus />
          Exclude People
        </Button>
      </HStack>

      {root.children.map((node, idx) => (
        <JunctionRuleEditor
          key={idx}
          node={node}
          onChange={(node) => onChange(replaceRootNodeChild(root, node, idx))}
          onRemove={
            idx === 0
              ? undefined
              : () => onChange(removeRootNodeChild(root, idx))
          }
          portalRef={portalRef}
        />
      ))}
    </Stack>
  );
}

interface RuleEditorProps {
  node: RetargetingRuleNode;
  onChange: (root: RetargetingRuleNode) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
}

function RuleEditor({ node, onChange, portalRef }: RuleEditorProps) {
  if (isURLNode(node)) {
    return (
      <URLRuleEditor node={node} onChange={onChange} portalRef={portalRef} />
    );
  }
  return (
    <JunctionRuleEditor node={node} onChange={onChange} portalRef={portalRef} />
  );
}

interface JunctionRuleEditorProps {
  node: RetargetingRuleNodeJunction;
  onChange: (root: RetargetingRuleNodeJunction) => void;
  onRemove?: () => void;
  portalRef?: React.RefObject<HTMLDivElement>;
}

const junctionTypes = createListCollection({
  items: [
    { label: "all", value: "and" },
    { label: "any", value: "or" },
  ],
});

function JunctionRuleEditor({
  node,
  onChange,
  onRemove,
  portalRef,
}: JunctionRuleEditorProps) {
  const label =
    node.mode === RetargetingRuleNodeMode.INCLUDE ? "Include" : "Exclude";

  return (
    <Stack width="full" css={{ "--field-label-width": "128px" }}>
      <HStack flex="1" gap="0">
        <Text>
          <strong>{label}</strong> people who meet{" "}
        </Text>
        <SelectRoot
          collection={junctionTypes}
          onValueChange={(e) => onChange(setJunctionNodeType(node, e.value[0]))}
          value={[node.type]}
          size="xs"
          mx="1"
          w="2xs"
        >
          <SelectTrigger>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {junctionTypes.items.map((item) => (
              <SelectItem item={item} key={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        <Text> of these criteria.</Text>
        <Spacer />
        {onRemove && (
          <IconButton
            colorPalette="gray"
            onClick={onRemove}
            size="xs"
            variant="surface"
          >
            <LuX />
          </IconButton>
        )}
      </HStack>
      <Field label="Audience Retention" orientation="horizontal">
        <InputGroup flex="1" endElement={<Text> days</Text>}>
          <Input
            size="xs"
            type="number"
            min="1"
            max="180"
            defaultValue={node.retention}
            onChange={(e) => {
              onChange({
                ...node,
                retention: Math.min(Math.max(1, Number(e.target.value)), 180),
              });
            }}
          />
        </InputGroup>
      </Field>
      <Stack borderRadius="sm" borderWidth="1" width="full" p="2">
        {node.children.map((child, idx) => (
          <HStack key={idx}>
            <RuleEditor
              node={child}
              onChange={(child) =>
                onChange(replaceJunctionNodeChild(node, child, idx))
              }
              portalRef={portalRef}
            />
            {idx > 0 && (
              <IconButton
                colorPalette="gray"
                size="xs"
                variant="surface"
                onClick={() => onChange(removeJunctionNodeChild(node, idx))}
              >
                <LuX />
              </IconButton>
            )}
          </HStack>
        ))}
        <Button
          colorPalette="gray"
          fontWeight="normal"
          variant="surface"
          size="xs"
          onClick={() => onChange(pushJunctionNodeChild(node))}
        >
          <LuPlus /> Add More Requirements
        </Button>
      </Stack>
    </Stack>
  );
}

const urlOperators = createListCollection({
  items: [
    { label: "contains", value: "contains" },
    { label: "does not contain", value: "not-contains" },
  ],
});

interface URLRuleEditorProps {
  node: RetargetingRuleNodeURL;
  onChange: (root: RetargetingRuleNodeURL) => void;
  portalRef?: React.RefObject<HTMLDivElement>;
}

function URLRuleEditor({ node, onChange, portalRef }: URLRuleEditorProps) {
  return (
    <HStack>
      <Text fontWeight="bold">URL</Text>
      <SelectRoot
        collection={urlOperators}
        value={[node.operator]}
        onValueChange={(e) => onChange(setURLNodeOperator(node, e.value[0]))}
        size="xs"
        mx="1"
        w="md"
      >
        <SelectTrigger>
          <SelectValueText />
        </SelectTrigger>
        <SelectContent portalRef={portalRef}>
          {urlOperators.items.map((item) => (
            <SelectItem item={item} key={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
      <Input
        size="xs"
        type="text"
        defaultValue={node.value}
        onChange={(e) => onChange({ ...node, value: e.target.value })}
      />
    </HStack>
  );
}
